/* Diamond Studio — collection page CRO helpers (2026-09-29)
   1. Delivery estimate on each card ("Est. arrival Oct 13–17"), from the store's own
      promise: every piece is made new and arrives about 2–2½ weeks after ordering.
   2. One real customer review (with photo) placed inside the product grid.
   3. Phones: a floating "Filters" button once the filter bar has scrolled away.
   4. Desktop: shorter "View details" label for the second card button.
   5. Gold chosen on a card is carried to the product page (?gold=18K%20Yellow).
   6. "Estimated arrival: 2–2½ weeks from ordering" also shows the actual dates (product page, bag, offer window).
   7. "Recently viewed" row at the top of collection pages for returning shoppers. */
(() => {
  'use strict';
  if (window.DSCardCRO) return;

  /* ---------- 1. Delivery estimate ---------- */
  const MIN_DAYS = 14;   // 2 weeks
  const MAX_DAYS = 18;   // 2½ weeks, rounded up
  const fmt = new Intl.DateTimeFormat('en-US', {month: 'short', day: 'numeric'});
  const addDays = days => { const d = new Date(); d.setDate(d.getDate() + days); return d; };
  const from = addDays(MIN_DAYS);
  const to = addDays(MAX_DAYS);
  const etaText = 'Est. arrival ' + (from.getMonth() === to.getMonth()
    ? fmt.format(from) + '–' + to.getDate()
    : fmt.format(from) + ' – ' + fmt.format(to));
  const etaTitle = 'Every piece is made new for you. Estimated arrival about 2–2½ weeks from ordering (not a guarantee).';

  function fillEta(root) {
    (root || document).querySelectorAll('[data-ds-cc-eta][hidden]').forEach(line => {
      const date = line.querySelector('[data-ds-cc-eta-date]');
      if (date) date.textContent = etaText;
      line.title = etaTitle;
      line.hidden = false;
    });
  }

  /* ---------- 2. Customer review inside the grid ---------- */
  // Reviews come from the Judge.me store reviews already on the page (snippets/ds-store-rating.liquid).
  function pickReview() {
    const tpl = document.getElementById('ds-store-rating-panel');
    if (!tpl) return null;
    const items = [...tpl.content.querySelectorAll('.ds-sr-review')].map(item => {
      const photo = item.querySelector('.ds-sr-review__photo');
      const body = (item.querySelector('.ds-sr-review__body')?.textContent || '').replace(/\s+/g, ' ').trim();
      const stars = item.querySelector('.ds-sr-stars');
      const rating = parseFloat(stars?.style.getPropertyValue('--ds-sr')) || 5;
      return {
        name: (item.querySelector('.ds-sr-review__name')?.textContent || '').trim(),
        body,
        rating,
        photo: photo ? photo.getAttribute('href') : '',
        thumb: photo?.querySelector('img')?.getAttribute('src') || ''
      };
    }).filter(r => r.photo && r.rating >= 5 && r.body.length >= 60 && r.body.length <= 420);
    return items[0] || null;
  }

  function placeReview() {
    const grid = document.querySelector('.ds-collection [data-dsc-grid]');
    if (!grid || grid.querySelector('.ds-cc-review')) return;
    const cards = grid.querySelectorAll(':scope > [data-dsc-card]');
    if (cards.length < 8) return;
    const review = pickReview();
    if (!review) return;
    const data = document.getElementById('ds-store-rating-data');
    let count = 0;
    try { count = JSON.parse(data?.textContent || '{}').count || 0; } catch (e) { /* no count */ }
    const tile = document.createElement('aside');
    tile.className = 'ds-cc-review';
    tile.setAttribute('aria-label', 'Customer review');
    const esc = s => s.replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
    tile.innerHTML =
      '<a class="ds-cc-review__photo" href="' + esc(review.photo) + '" target="_blank" rel="noopener">' +
        '<img src="' + esc(review.photo) + '" alt="Photo shared by ' + esc(review.name || 'a customer') + '" loading="lazy" width="600" height="600"></a>' +
      '<div class="ds-cc-review__copy">' +
        '<p class="ds-cc-review__eyebrow">From our customers</p>' +
        '<span class="ds-sr-stars" style="--ds-sr: ' + review.rating + '" role="img" aria-label="' + review.rating + ' out of 5 stars"></span>' +
        '<blockquote><p>“' + esc(review.body) + '”</p></blockquote>' +
        '<p class="ds-cc-review__name">' + esc(review.name || 'Customer') + ' <small>· Diamond Studio customer</small></p>' +
        (count ? '<button type="button" class="ds-cc-review__more" data-ds-sr-open>Read all ' + count + ' reviews</button>' : '') +
      '</div>';
    // After the 18th piece. The grid already has the payment note after the 6th piece and
    // "Not sure which piece is right?" after the 12th, so this keeps one extra tile every 6 pieces.
    const anchor = cards[Math.min(17, cards.length - 1)];
    anchor.after(tile);
  }

  /* ---------- 3. Phones: floating Filters button ---------- */
  function setupFilterPill() {
    const toolbar = document.querySelector('.ds-collection .dsc-toolbar');
    const open = toolbar?.querySelector('[data-dsc-open-filters]');
    const grid = document.querySelector('.ds-collection [data-dsc-grid]');
    if (!toolbar || !open || !grid || document.querySelector('.ds-cc-filter-pill')) return;
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'ds-cc-filter-pill';
    pill.setAttribute('aria-label', 'Filter and sort pieces');
    pill.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M4 7h10M18 7h2M4 17h4M12 17h8"/><circle cx="16" cy="7" r="2"/><circle cx="10" cy="17" r="2"/></svg><span>Filters</span><span class="ds-cc-filter-pill__count"></span>';
    const count = pill.querySelector('.ds-cc-filter-pill__count');
    const source = toolbar.querySelector('[data-dsc-filter-count]');
    const syncCount = () => { count.textContent = (source?.textContent || '').replace(/[^0-9]/g, ''); };
    syncCount();
    if (source) new MutationObserver(syncCount).observe(source, {childList: true, characterData: true, subtree: true});
    pill.addEventListener('click', () => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({event: 'ds_filter_pill_open'});
      open.click();
    });
    document.body.append(pill);
    // The filter bar is "sticky" but sits under the sticky header on phones, so it is out of reach
    // once the shopper scrolls into the pieces. Show the button while the grid fills the screen.
    let queued = false;
    const update = () => {
      queued = false;
      const rect = grid.getBoundingClientRect();
      pill.classList.toggle('is-visible', rect.top < 120 && rect.bottom > window.innerHeight * 0.6);
    };
    window.addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(update); } }, {passive: true});
    window.addEventListener('resize', update, {passive: true});
    update();
  }

  /* ---------- 4. Desktop: shorter label for the second card button ---------- */
  // "View product & options" wrapped onto two lines once it sat next to "Make an offer".
  function shortenLinks(root) {
    (root || document).querySelectorAll('.ds-studio-offer-zone > a.ds-studio-product-link:not([data-cc-short])').forEach(link => {
      const label = [...link.childNodes].find(node => node.nodeType === 3 && node.nodeValue.trim());
      if (label) label.nodeValue = 'View details ';
      link.dataset.ccShort = '1';
    });
  }

  /* ---------- 5. Carry the gold chosen on a card to the product page ---------- */
  document.addEventListener('click', event => {
    const swatch = event.target.closest?.('.ds-cc-picker [data-dsc-gold]');
    if (!swatch) return;
    const card = swatch.closest('[data-dsc-card]');
    const label = swatch.getAttribute('aria-label') || '';
    if (!card || !label) return;
    // Let the collection script update the price first, then point every product link at this gold.
    setTimeout(() => {
      // Keep the "% OFF" badge true for the chosen gold (the upgrade changes the percentage slightly).
      try {
        const data = JSON.parse(card.querySelector('[data-dsc-product]').textContent);
        const option = (data.gold || []).find(g => g.label === label);
        const badge = card.querySelector('.ds-cc-off');
        const price = option ? option.price : data.price;
        const compare = data.compare ? data.compare + (option ? option.extra : 0) : 0;
        if (badge && compare > price) badge.textContent = Math.round((compare - price) / compare * 100) + '% OFF';
      } catch (e) { /* keep the badge as rendered */ }
      card.querySelectorAll('a[href*="/products/"]').forEach(link => {
        try {
          const url = new URL(link.getAttribute('href'), location.origin);
          url.searchParams.set('gold', label);
          link.setAttribute('href', url.pathname + url.search + url.hash);
        } catch (e) { /* leave the link as it is */ }
      });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({event: 'ds_card_gold_select', gold: label});
    }, 0);
  });

  /* ---------- 6. Show the actual arrival dates next to "2–2½ weeks" ---------- */
  const datesOnly = etaText.replace('Est. arrival ', '');
  const ARRIVAL = [
    'Estimated arrival: 2–2½ weeks from ordering.',
    'Estimated arrival: 2–2½ weeks after ordering.'
  ];
  const arrivalDone = new WeakSet();
  function patchArrival(root) {
    const scope = root || document.body;
    if (!scope || !(scope.textContent || '').includes('2–2½ weeks')) return;
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    const hits = [];
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!arrivalDone.has(node) && ARRIVAL.some(t => node.nodeValue.includes(t))) hits.push(node);
    }
    hits.forEach(node => {
      ARRIVAL.forEach(t => { node.nodeValue = node.nodeValue.replace(t, 'Estimated arrival: ' + datesOnly + ' if you order today.'); });
      arrivalDone.add(node);
    });
  }
  new MutationObserver(records => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType === 1 && (node.textContent || '').includes('2–2½ weeks')) patchArrival(node);
        else if (node.nodeType === 3 && node.parentNode && (node.nodeValue || '').includes('2–2½ weeks')) patchArrival(node.parentNode);
      }
    }
  }).observe(document.documentElement, {childList: true, subtree: true});

  /* ---------- 7. Recently viewed ---------- */
  const RECENT_KEY = 'ds-recently-viewed-v1';
  const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch (e) { return []; } };
  function rememberProduct() {
    const match = location.pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return;
    const handle = decodeURIComponent(match[1]);
    if (/gift-card|^option-set-/.test(handle)) return;
    const list = readRecent().filter(item => item.h !== handle);
    list.unshift({h: handle, t: Date.now()});
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12))); } catch (e) { /* storage unavailable */ }
  }
  const money = cents => {
    const cur = window.DS_CRO?.currency || 'USD';
    const amount = (cents / 100).toLocaleString('en-US', {minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2});
    return (cur === 'USD' ? '$' : '') + amount + (window.DS_CRO?.country === 'US' && cur === 'USD' ? '' : ' ' + cur);
  };
  async function showRecent() {
    const grid = document.querySelector('.ds-collection [data-dsc-grid]');
    if (!grid || document.querySelector('.ds-cc-recent')) return;
    const handles = readRecent().filter(item => Date.now() - item.t < 1000 * 60 * 60 * 24 * 60).slice(0, 8).map(item => item.h);
    if (!handles.length) return;
    const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const results = await Promise.allSettled(handles.map(h => fetch(root + 'products/' + encodeURIComponent(h) + '.js').then(r => r.ok ? r.json() : null)));
    const items = results.map(r => r.status === 'fulfilled' ? r.value : null).filter(p => p && p.available && p.featured_image);
    if (!items.length || document.querySelector('.ds-cc-recent')) return;
    const esc = t => String(t).replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
    const img = src => { const base = src.startsWith('//') ? 'https:' + src : src; return base + (base.includes('?') ? '&' : '?') + 'width=240'; };
    const section = document.createElement('section');
    section.className = 'ds-cc-recent';
    section.setAttribute('aria-labelledby', 'ds-cc-recent-title');
    section.innerHTML = '<h2 class="ds-cc-recent__title" id="ds-cc-recent-title">Recently viewed</h2><ul class="ds-cc-recent__list" role="list">' +
      items.map(p => '<li><a href="' + esc(p.url) + '" data-ds-cc-recent><img src="' + esc(img(p.featured_image)) + '" alt="" width="120" height="120" loading="lazy"><span class="ds-cc-recent__name">' + esc(p.title) + '</span><span class="ds-cc-recent__price">' + esc(money(p.price)) + '</span></a></li>').join('') +
      '</ul>';
    section.addEventListener('click', event => {
      if (!event.target.closest('[data-ds-cc-recent]')) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({event: 'ds_recently_viewed_click'});
    });
    grid.before(section);
  }

  function run() {
    fillEta();
    shortenLinks();
    placeReview();
    setupFilterPill();
    patchArrival();
    rememberProduct();
    showRecent();
  }

  // "Load more" adds cards later; fill their delivery dates too.
  const watchGrid = () => {
    const grid = document.querySelector('.ds-collection [data-dsc-grid]');
    if (grid) new MutationObserver(() => { fillEta(grid); shortenLinks(grid); }).observe(grid, {childList: true});
  };

  const start = () => { run(); watchGrid(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();

  window.DSCardCRO = {etaText, fillEta};
})();
